import { useGenericAPI, useSingleGenericAPI } from "../../../utils/genericAPI/useGenericAPI";
import { Meter, MeterPatch, SerialManagementExtended } from "../types/meter.types";
import { APIGenerator, getAllParam } from "../../../utils/genericAPI/GenerateAPI";
import { useAuth } from "../../auth/hooks";
import { useQuery } from "react-query";
import meterAPI from "../services/meterAPI";
import { AxiosError } from "axios";
import { convertQueryString, convertWhereQueryToFilterObject } from "../../../utils/genericAPI/queryURL.utils";
import { ResponseAPIWithPagination } from "../../../common/types/API.types";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL + "/api/v1";

// API Generator instance for meters
const meterAPIObject = new APIGenerator<Meter, MeterPatch, MeterPatch>(`${BACKEND_URL}/meters`);

// Custom API Generator for jouly meters that uses 'where' and 'orderBy' instead of 'whereQuery' and 'orderByQuery'
class JoulyMeterAPIGenerator<T, X, V> extends APIGenerator<T, X, V> {
    private joulyBaseURL: string;

    constructor(baseURL: string, axiosConfig?: any) {
        super(baseURL, axiosConfig);
        this.joulyBaseURL = baseURL;
    }

    public async getAll({
        page = 0,
        pageSize = 0,
        filter,
        orderBy,
        search,
        filterQuery,
    }: getAllParam<V>) {
        const whereQuery = filterQuery || "";
        const query: { [key: string]: string } = {
            currentPage: page.toString(), // useGenericAPI already passes 1-based page numbers
            pageSize: pageSize.toString(),
        };
        
        if (whereQuery) {
            query.where = whereQuery;
        }
        
        if (orderBy) {
            query.orderBy = orderBy;
        }
        
        if (search) {
            query.query = search;
        }
        
        const url = convertQueryString(this.joulyBaseURL, query);
        return (await axios.get<ResponseAPIWithPagination<T>>(url)).data;
    }
}

// API Generator instance for jouly meters
const joulyMeterAPIObject = new JoulyMeterAPIGenerator<SerialManagementExtended, any, any>(`${BACKEND_URL}/meters/jouly/meters`);

export const useMeter = () => {
    useAuth().setHeaderAuthToRequest()

    const {
        getAllQuery,
        createNew,
        deleteByIdQuery,
    } = useGenericAPI<Meter, MeterPatch, MeterPatch>({
        APIname: "meters",
        APIObject: meterAPIObject,
        pageSize: 10,
        orderBy: "-mtr_updated_at",
    });

    return {
        allMeterQuery: getAllQuery,
        createNewMeter: createNew,
        deleteMeter: deleteByIdQuery,
    };
};

export const useSingleMeter = (meterId?: string) => {

    useAuth().setHeaderAuthToRequest()

    const {
        singleQuery,
        updateQuery,
        deleteQuery,
    } = useSingleGenericAPI<Meter, MeterPatch>({
        APIname: "meters",
        APIObject: meterAPIObject,
        id: meterId || "",
    });

    return {
        singleMeterQuery: singleQuery,
        updateMeter: updateQuery,
        deleteMeter: deleteQuery,
    };
};

export const useMeter8000Data = (meterId?: string) => {
    useAuth().setHeaderAuthToRequest()
    const getClient8000ByMeterIdQuery = useQuery({
        queryKey: ['client8000', meterId],
        queryFn: () => meterAPI.getClient8000ByMeterId(meterId || "none"),
        enabled: !!meterId && meterId !== "",
    })

    const getMeterInfoLocalizationByMeterIdQuery = useQuery({
        queryKey: ['meterInfoLocalization', meterId],
        queryFn: () => meterAPI.getMeterInfoLocalizationByMeterId(meterId || "none"),
        enabled: !!meterId && meterId !== "",
    })

    return {
        getClient8000ByMeterIdQuery,
        getMeterInfoLocalizationByMeterIdQuery,
    }
}

// Liaisons reelles compteur->poteau du compte (1 seul fetch, partage entre toutes
// les lignes via le cache react-query). Chaque cellule "Poteau" lit ce resultat.
export const useMeterPoleLinks = () => {
    useAuth().setHeaderAuthToRequest()
    return useQuery({
        queryKey: ['meterPoleLinks'],
        queryFn: () => meterAPI.getMeterPoleLinks(),
        staleTime: 5 * 60 * 1000,
    })
}

export const useAllJoulyMeters = (enabled: boolean) => {
    useAuth().setHeaderAuthToRequest()
    const query = useQuery({
        queryKey: ['jouly-meters-all'],
        queryFn: () => meterAPI.getJoulyMeters(1, 5000),
        enabled,
        staleTime: 60_000,
    })
    return query
}

export const useMeterV2 = () => {
    useAuth().setHeaderAuthToRequest();

    const {
        getAllQuery,
    } = useGenericAPI<SerialManagementExtended, any, any>({
        APIname: "joulyMeters",
        APIObject: joulyMeterAPIObject,
        pageSize: 10,
        
    });

    // Map the generic API response to match the expected interface
    const joulyMetersQuery = {
        ...getAllQuery,
        data: getAllQuery.data,
        error: getAllQuery.error as AxiosError,
        setPage: (page: number) => {
            // Convert from 0-based to 1-based page number if needed
            getAllQuery.setPage(page);
        },
        setPageSize: getAllQuery.setPageSize,
        setOrderBy: (order: string | undefined) => {
            getAllQuery.setOrderBy(order || "");
        },
        setWhere: (whereParam: string | undefined) => {
            if (whereParam) {
                // Convert where string to filter object
                const filterObject = convertWhereQueryToFilterObject<any>(whereParam);
                getAllQuery.setFilterObject(filterObject);
            } else {
                getAllQuery.setFilterObject(undefined);
            }
        },
        setSearch: getAllQuery.setSearch,
        sreachValue: getAllQuery.sreachValue,
        currentPage: getAllQuery.data?.pagination?.current_page || 1,
        pageSize: getAllQuery.data?.pagination?.page_size || 10,
    };

    return {
        joulyMetersQuery,
    };
};